import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {createProductServiceRepository} from "../data/repositories";
import {ProductServiceWorkDelivery} from "../domain/product-service.types";
import {GET_STORE_SERVICE_ORDER_RQ_KEY} from "../../../react-query-hooks/store/services/constants";
import {GetStoreServiceOrderApiResponse} from "../../../react-query-hooks/store/services/get-store-service-order/types";

type LegacyDelivery =
  GetStoreServiceOrderApiResponse["order"]["productServiceDeliveries"][number];

/**
 * Maps a canonical work delivery to the legacy `productServiceDeliveries` item.
 */
const toLegacyDelivery = (d: ProductServiceWorkDelivery): LegacyDelivery => ({
  id: d.id,
  userId: 0,
  productServiceTransactionId: 0,
  statusId: d.statusId,
  statusName: d.statusName,
  deliveryDate: d.deliveryDate ?? "",
  delivery: d.delivery,
  customer_comment: d.customerComment,
  createdAt: d.createdAt,
  updatedAt: d.updatedAt,
});

/**
 * Legacy-compatible order-detail hook.
 *
 * The v2 order-detail endpoint returns ONLY the work-delivery submissions (no
 * order object), so this hook supplies just `order.productServiceDeliveries`;
 * the remaining order-level fields are defaulted. Consumers now read the order's
 * own fields from the selected list item (`orderDetails`), which carries them
 * (incl. `emailAddress`). Query key preserved.
 *
 * @deprecated Use useProductServiceOrder from features/product-services instead.
 */
export const useGetStoreServiceOrderApi = (storeServiceOrderId?: number) => {
  const repository = createProductServiceRepository();

  return useQuery<
    GetStoreServiceOrderApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_STORE_SERVICE_ORDER_RQ_KEY, String(storeServiceOrderId)],
    queryFn: async () => {
      const deliveries = await repository.getOrderDeliveries(storeServiceOrderId!);

      return {
        order: {
          id: storeServiceOrderId ?? 0,
          userId: 0,
          productServiceId: 0,
          firstName: "",
          lastName: "",
          emailAddress: "",
          currency: "",
          amount: "0",
          charges: "0",
          reference: "",
          processor_reference: null,
          statusId: 0,
          is_paid: true,
          payment_channel: "paystack",
          discount_code: null,
          is_discounted: false,
          charge_bearer: "customer",
          booking_questions: null,
          delivery_date: "",
          revision_requested: false,
          createdAt: "",
          updatedAt: "",
          productServiceDeliveries: deliveries.map(toLegacyDelivery),
        },
      };
    },
    enabled: !!storeServiceOrderId,
  });
};
